import { User, UserManager } from 'oidc-client-ts';
import { buildCognitoSettings, readEnv } from './cognitoConfig';

// Lazily-constructed singleton. `null` means auth is disabled (mock/dev mode):
// callers treat that as "no session required" so the app runs without a login.
let manager: UserManager | null | undefined;

export const getUserManager = (): UserManager | null => {
  if (manager === undefined) {
    const settings = buildCognitoSettings();
    manager = settings ? new UserManager(settings) : null;
  }
  return manager;
};

/** True when a Cognito pool is configured and the SPA must authenticate. */
export const isAuthEnabled = (): boolean => getUserManager() !== null;

/**
 * The bearer token sent to the Go API. We send the **ID token**, not the access
 * token: the API's Cognito verifier reads `email` (present only on the ID
 * token) and may be hardened with `COGNITO_AUDIENCE=<clientId>`, which only the
 * ID token's `aud` claim satisfies. Both tokens carry `sub` + `cognito:groups`.
 */
const bearerFor = (user: User | null): string | null =>
  user && !user.expired ? user.id_token ?? null : null;

export const getCurrentBearer = async (): Promise<string | null> => {
  const um = getUserManager();
  if (!um) return null;
  const user = await um.getUser();
  return bearerFor(user);
};

export const refreshBearer = async (): Promise<string | null> => {
  const um = getUserManager();
  if (!um) return null;
  try {
    const user = await um.signinSilent();
    return bearerFor(user);
  } catch {
    return null;
  }
};

/** Kick off the Hosted UI redirect, remembering where to return afterwards. */
export const startLogin = async (returnTo?: string): Promise<void> => {
  const um = getUserManager();
  if (!um) return;
  await um.signinRedirect({
    state: returnTo ?? window.location.pathname + window.location.search,
  });
};

/** Complete the Authorization Code exchange on the /auth/callback route. */
export const completeLogin = async (): Promise<string> => {
  const um = getUserManager();
  if (!um) return '/';
  const user = await um.signinRedirectCallback();
  const returnTo = typeof user.state === 'string' ? user.state : '/';
  // Never bounce back into the callback route itself.
  return returnTo.startsWith('/auth/callback') ? '/' : returnTo;
};

// Cognito's /logout endpoint expects `logout_uri` (+ `client_id`), not the
// OIDC-standard `post_logout_redirect_uri` oidc-client-ts sends by default.
export const logout = async (): Promise<void> => {
  const um = getUserManager();
  if (!um) return;
  const env = readEnv();
  await um.signoutRedirect({
    extraQueryParams: {
      logout_uri: env?.postLogoutRedirectUri ?? '',
      client_id: env?.clientId ?? '',
    },
  });
};
